import React from "react";
import Modal from "components/ui/Modal";
import ConfirmDialog from "components/ui/ConfirmDialog";
import SearchableFormSelect from "components/ui/SearchableFormSelect";
import { toastError } from "lib/toast";
import { adminService } from "services/adminService";
import type {
  StudentCourseEnrollment,
  StudentCourseOption,
} from "services/adminStudentCoursesService";

type Props = {
  open: boolean;
  onClose: () => void;
  studentId: number | null;
  studentName: string;
  isArabic: boolean;
};

function dateAfterDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export default function StudentCoursesModal({
  open,
  onClose,
  studentId,
  studentName,
  isArabic,
}: Props) {
  const [courses, setCourses] = React.useState<StudentCourseEnrollment[]>([]);
  const [options, setOptions] = React.useState<StudentCourseOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [selectedCourseId, setSelectedCourseId] = React.useState<number | null>(null);
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [editingCourseId, setEditingCourseId] = React.useState<number | null>(null);
  const [editingDates, setEditingDates] = React.useState<Record<number, { start: string; end: string }>>({});
  const [removeTarget, setRemoveTarget] = React.useState<StudentCourseEnrollment | null>(null);

  const load = React.useCallback(async () => {
    if (studentId === null) return;
    setLoading(true);
    try {
      const [enrollments, available] = await Promise.all([
        adminService.getStudentCourses(studentId),
        adminService.getStudentCourseOptions(studentId),
      ]);
      setCourses(enrollments);
      setOptions(available);
      setSelectedCourseId(available[0]?.id ?? null);
      setStartDate((value) => value || new Date().toISOString().slice(0, 10));
      setEndDate((value) => value || dateAfterDays(30));
      setEditingDates(
        Object.fromEntries(
          enrollments.map((course) => [course.course_id, { start: course.start_date || "", end: course.end_date || "" }])
        )
      );
    } catch (error) {
      console.error("Failed to load student courses:", error);
      toastError(isArabic ? "تعذر تحميل كورسات الطالب." : "Could not load student courses.");
    } finally {
      setLoading(false);
    }
  }, [isArabic, studentId]);

  React.useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const attach = async () => {
    if (studentId === null || selectedCourseId === null) return;
    setSaving(true);
    try {
      await adminService.attachStudentCourse(studentId, selectedCourseId, {
        start_date: startDate || null,
        end_date: endDate || null,
      });
      await load();
    } catch (error) {
      console.error("Failed to attach student course:", error);
      toastError(isArabic ? "تعذر إضافة الكورس للطالب." : "Could not add the course.");
    } finally {
      setSaving(false);
    }
  };

  const updateDates = async (course: StudentCourseEnrollment) => {
    if (studentId === null) return;
    const dates = editingDates[course.course_id] || { start: "", end: "" };
    setSaving(true);
    try {
      await adminService.updateStudentCourse(studentId, course.course_id, {
        start_date: dates.start || null,
        end_date: dates.end || null,
      });
      setEditingCourseId(null);
      await load();
    } catch (error) {
      console.error("Failed to update student course:", error);
      toastError(isArabic ? "تعذر تحديث مدة الكورس." : "Could not update the course duration.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (studentId === null || removeTarget === null) return;
    setSaving(true);
    try {
      await adminService.removeStudentCourse(studentId, removeTarget.course_id);
      setRemoveTarget(null);
      await load();
    } catch (error) {
      console.error("Failed to remove student course:", error);
      toastError(isArabic ? "تعذر إزالة الكورس من الطالب." : "Could not remove the course.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="xl"
        title={isArabic ? `كورسات الطالب: ${studentName}` : `Student courses: ${studentName}`}
        footer={
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
          >
            {isArabic ? "إغلاق" : "Close"}
          </button>
        }
      >
        {loading ? (
          <p className="py-10 text-center text-sm text-gray-500">{isArabic ? "جارٍ التحميل..." : "Loading..."}</p>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
              <h4 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
                {isArabic ? "إضافة كورس للطالب" : "Add a course"}
              </h4>
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_150px_150px_auto] md:items-end">
                <div>
                  <p className="mb-1 text-xs font-semibold text-gray-500">{isArabic ? "الكورس" : "Course"}</p>
                  <SearchableFormSelect
                    value={selectedCourseId ?? 0}
                    onChange={(value) => setSelectedCourseId(value ? Number(value) : null)}
                    options={options.map((course) => ({
                      value: course.id,
                      label: course.subject?.name ? `${course.title} · ${course.subject.name}` : course.title,
                    }))}
                    isArabic={isArabic}
                    numeric
                    emptyNumber={null}
                    placeholder={isArabic ? "اختر كورسًا" : "Select a course"}
                  />
                </div>
                <label className="text-xs font-semibold text-gray-500">
                  {isArabic ? "من" : "Start"}
                  <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} className="ef-input mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm dark:bg-navy-900 dark:text-white" />
                </label>
                <label className="text-xs font-semibold text-gray-500">
                  {isArabic ? "إلى" : "End"}
                  <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} className="ef-input mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm dark:bg-navy-900 dark:text-white" />
                </label>
                <button type="button" disabled={saving || selectedCourseId === null} onClick={() => void attach()} className="rounded-lg bg-brand-500 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
                  {isArabic ? "إضافة" : "Add"}
                </button>
              </div>
              {options.length === 0 ? <p className="mt-2 text-xs text-gray-500">{isArabic ? "لا توجد كورسات متاحة للإضافة." : "No more courses are available."}</p> : null}
            </div>

            <div>
              <h4 className="mb-3 text-sm font-bold text-navy-700 dark:text-white">
                {isArabic ? `الكورسات الحالية (${courses.length})` : `Current courses (${courses.length})`}
              </h4>
              {courses.length === 0 ? (
                <p className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-white/10">{isArabic ? "الطالب غير مسجل في أي كورس." : "The student is not enrolled in any course."}</p>
              ) : (
                <div className="space-y-3">
                  {courses.map((course) => {
                    const dates = editingDates[course.course_id] || { start: "", end: "" };
                    const editing = editingCourseId === course.course_id;
                    return (
                      <div key={course.course_id} className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-navy-700 dark:text-white">{course.title}</p>
                            <p className="text-xs text-gray-500">{course.subject_name || ""}{course.instructor_name ? ` · ${course.instructor_name}` : ""}</p>
                            {course.source_type ? <span className="mt-1 inline-block rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-semibold text-purple-700 dark:bg-purple-500/10 dark:text-purple-200">{isArabic ? "من باقة" : "Bundle access"}</span> : null}
                          </div>
                          <div className="flex gap-2">
                            <button type="button" disabled={saving} onClick={() => setEditingCourseId(editing ? null : course.course_id)} className="rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-200">{editing ? (isArabic ? "إلغاء" : "Cancel") : (isArabic ? "تعديل المدة" : "Edit dates")}</button>
                            <button type="button" disabled={saving} onClick={() => setRemoveTarget(course)} className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-200">{isArabic ? "إزالة" : "Remove"}</button>
                          </div>
                        </div>
                        {editing ? (
                          <div className="mt-3 grid gap-2 md:grid-cols-[1fr_1fr_auto] md:items-end">
                            <label className="text-xs font-semibold text-gray-500">{isArabic ? "من" : "Start"}<input type="date" value={dates.start} onChange={(event) => setEditingDates((previous) => ({ ...previous, [course.course_id]: { ...dates, start: event.target.value } }))} className="ef-input mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm dark:bg-navy-900 dark:text-white" /></label>
                            <label className="text-xs font-semibold text-gray-500">{isArabic ? "إلى" : "End"}<input type="date" value={dates.end} onChange={(event) => setEditingDates((previous) => ({ ...previous, [course.course_id]: { ...dates, end: event.target.value } }))} className="ef-input mt-1 w-full rounded-lg border border-gray-200 px-2 py-2 text-sm dark:bg-navy-900 dark:text-white" /></label>
                            <button type="button" disabled={saving} onClick={() => void updateDates(course)} className="rounded-lg bg-brand-500 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50">{isArabic ? "حفظ" : "Save"}</button>
                          </div>
                        ) : (
                          <p className="mt-3 text-xs text-gray-600 dark:text-gray-300">{isArabic ? "المدة: " : "Duration: "}{course.start_date || "—"} → {course.end_date || "—"}{course.payment_status ? ` · ${course.payment_status}` : ""}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={remove}
        title={isArabic ? "إزالة الكورس من الطالب؟" : "Remove course from student?"}
        description={removeTarget ? `${removeTarget.title} — ${isArabic ? "سيُمنع الطالب من الوصول إليه." : "The student will lose access."}` : undefined}
        confirmLabel={isArabic ? "إزالة" : "Remove"}
      />
    </>
  );
}
